import { useState } from "react";
import { X, Loader2, ChevronRight, ChevronDown, FileText, Plus, MessageSquare } from "lucide-react";
import {
  useProjectTree,
  useUpdateProjectNotes,
  PROMPT_REPORT_LABELS,
  type PromptReportType,
  type SnapshotTrigger,
  type ProjectNote,
} from "@/lib/api";

interface ProjectTreePanelProps {
  jobId: string;
  currentPersona: string;
  isOpen: boolean;
  onClose: () => void;
  onSelectReport: (reportType: PromptReportType, persona: string) => void;
}

// Refine-panel context fields — excluded from the brief card AND the Context
// expansion so both show only genuine scenario brief settings (tone, angle,
// publication_type, word_count_target, show_name, episode_topic, …). These are
// the actual snake_case keys that land in brief.data plus the manifest's
// refinement_context keys; the Refine panel writes context/language/
// summary_length/target_platform into brief.data, which must stay hidden here.
const REFINEMENT_CONTEXT_KEYS = new Set([
  "context",
  "language",
  "summary_length",
  "target_platform",
  "recording_description",
  "target_audience",
  "output_language",
  "key_terms",
  "speaker_names",
]);

const TRIGGER_BADGES: Record<SnapshotTrigger, { label: string; cls: string; border: string }> = {
  initial: { label: "Initial", cls: "bg-gray-100 text-gray-600", border: "border-l-gray-300" },
  chat: { label: "Chat", cls: "bg-blue-100 text-blue-700", border: "border-l-green-400" },
  refine: { label: "Refine", cls: "bg-purple-100 text-purple-700", border: "border-l-blue-400" },
  revert: { label: "Revert", cls: "bg-amber-100 text-amber-700", border: "border-l-yellow-400" },
  enrichment: { label: "Enrichment", cls: "bg-green-100 text-green-700", border: "border-l-orange-400" },
  user_upload: { label: "Upload", cls: "bg-teal-100 text-teal-700", border: "border-l-amber-500" },
};

export function ProjectTreePanel({
  jobId,
  currentPersona,
  isOpen,
  onClose,
  onSelectReport,
}: ProjectTreePanelProps) {
  const { data: tree, isLoading } = useProjectTree(isOpen ? jobId : undefined, isOpen);
  const updateNotes = useUpdateProjectNotes(jobId);
  const [addingNote, setAddingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [expandedManifest, setExpandedManifest] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Persona groups collapse by default; only the current persona starts open.
  const [expandedPersonas, setExpandedPersonas] = useState<Set<string>>(() => new Set([currentPersona]));

  if (!isOpen) return null;

  // Report ids are globally unique, so they key expansion safely even though a
  // report_type (e.g. SUMMARY) repeats across personas and languages.
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const togglePersona = (persona: string) =>
    setExpandedPersonas((prev) => {
      const next = new Set(prev);
      if (next.has(persona)) next.delete(persona);
      else next.add(persona);
      return next;
    });

  const personas = tree?.personas ?? [];
  const notes = tree?.project_notes ?? [];

  const saveNote = () => {
    const text = noteDraft.trim();
    if (!text) {
      setAddingNote(false);
      setNoteDraft("");
      return;
    }
    const next: ProjectNote[] = [
      ...notes,
      { id: crypto.randomUUID(), text, created_at: new Date().toISOString() },
    ];
    updateNotes.mutate(next);
    setNoteDraft("");
    setAddingNote(false);
  };

  const deleteNote = (id: string) => {
    updateNotes.mutate(notes.filter((n) => n.id !== id));
  };

  const cancelNote = () => {
    setNoteDraft("");
    setAddingNote(false);
  };

  return (
    <div className="w-[360px] border-l bg-background flex flex-col h-full overflow-hidden flex-shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">Project map</h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {isLoading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        )}

        {/* Project title */}
        {tree && (
          <div className="text-xs font-semibold truncate px-1">{tree.title || "Untitled project"}</div>
        )}

        {/* Working notes (D-2) — free-form notes above the tree */}
        {tree && (
          notes.length === 0 && !addingNote ? (
            <button
              onClick={() => setAddingNote(true)}
              className="flex items-center gap-1 px-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <Plus className="w-3 h-3" /> Add note
            </button>
          ) : (
            <div className="border border-dashed border-gray-300 rounded px-2 py-1.5 mt-1 mb-2 space-y-1.5">
              <div className="text-[11px] text-muted-foreground font-medium">Notes</div>
              {notes.map((n) => (
                <div key={n.id} className="flex items-start gap-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-foreground whitespace-pre-wrap break-words">{n.text}</p>
                    <span className="text-[10px] text-gray-400">
                      {new Date(n.created_at).toLocaleString()}
                    </span>
                  </div>
                  <button
                    onClick={() => deleteNote(n.id)}
                    disabled={updateNotes.isPending}
                    className="text-gray-400 hover:text-red-500 shrink-0 disabled:opacity-40"
                    title="Delete note"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {addingNote ? (
                <textarea
                  autoFocus
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      saveNote();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      cancelNote();
                    }
                  }}
                  onBlur={cancelNote}
                  rows={2}
                  placeholder="Write a note… (Enter to save, Esc to cancel)"
                  className="w-full text-[11px] border border-gray-200 rounded px-1.5 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-primary/40"
                />
              ) : (
                <button
                  onClick={() => setAddingNote(true)}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  <Plus className="w-3 h-3" /> Add note
                </button>
              )}
            </div>
          )
        )}

        {/* Persona groups → reports → versions */}
        {personas.map((g) => {
          const personaOpen = expandedPersonas.has(g.persona);
          const totalReports = g.reports.length;
          const totalVersions = g.reports.reduce((sum, r) => sum + r.snapshot_count, 0);
          // Per-role brief: first snapshot (across this role's reports) that carries
          // any brief_data. Renders as a card directly under the role header.
          const roleBrief = g.reports
            .flatMap((r) => r.snapshots)
            .find((s) => s.brief_data && Object.keys(s.brief_data).length > 0)?.brief_data;
          // Only genuine brief settings — drop refinement_context fields and blanks.
          const roleBriefEntries = Object.entries(roleBrief ?? {}).filter(
            ([k, v]) => !REFINEMENT_CONTEXT_KEYS.has(k) && v !== null && v !== "" && v !== undefined,
          );
          // Align Map ordering and naming with the report tab bar by reusing the
          // backend's per-persona report_labels (same source as the job's
          // report_labels the tabs render from). Dict key order is the tab order;
          // types absent from it (e.g. SENTIMENT) sort last and fall back to the
          // generic label.
          const labelOrder = Object.keys(g.report_labels ?? {});
          const tabOrder = new Map(labelOrder.map((t, i) => [t, i]));
          const reportLabel = (type: PromptReportType) =>
            g.report_labels?.[type] ?? PROMPT_REPORT_LABELS[type] ?? type;
          const orderedReports = [...g.reports].sort(
            (a, b) =>
              (tabOrder.get(a.report_type) ?? 999) - (tabOrder.get(b.report_type) ?? 999),
          );
          return (
          <div key={g.persona} className="space-y-1.5">
            <button
              onClick={() => togglePersona(g.persona)}
              className="flex items-center gap-1.5 px-1 pt-1 w-full text-left hover:opacity-80"
            >
              {personaOpen ? (
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              )}
              {g.vertical && (
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{g.vertical}</span>
              )}
              {g.vertical && <span className="text-[10px] text-muted-foreground">·</span>}
              <span className="text-xs font-semibold">{g.display_name}</span>
              {g.persona === currentPersona && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">current</span>
              )}
              <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
                {totalReports} reports · {totalVersions} versions
              </span>
            </button>

            {/* Per-role brief card */}
            {roleBriefEntries.length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground border border-dashed border-gray-300 rounded px-2 py-1.5 mb-1">
                {roleBriefEntries.map(([k, v]) => (
                  <span key={k}>
                    <span className="font-medium capitalize">{k.replace(/_/g, ' ')}:</span>{' '}{String(v)}
                  </span>
                ))}
              </div>
            )}

            {personaOpen && orderedReports.map((r) => {
              const key = String(r.id);
              const isExp = expanded.has(key);
              return (
                <div key={r.id} className="rounded-md border">
                  <div className="flex items-center gap-1.5 px-2 py-2">
                    <button
                      onClick={() => toggle(key)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                      {isExp ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => onSelectReport(r.report_type, g.persona)}
                      className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
                      title="Open this report"
                    >
                      <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs font-medium truncate">
                        {reportLabel(r.report_type)}
                      </span>
                      {r.language && (
                        <span className="text-[9px] uppercase text-muted-foreground shrink-0">{r.language}</span>
                      )}
                    </button>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {r.snapshot_count} {r.snapshot_count === 1 ? "version" : "versions"}
                    </span>
                  </div>

                  {isExp && (
                    <div className="px-2 pb-2 space-y-1.5">
                      {r.snapshots.length === 0 && (
                        <p className="text-[10px] text-muted-foreground px-1 py-1">No versions tracked yet.</p>
                      )}
                      {/* Backend already sends snapshots version-descending. */}
                      {r.snapshots.map((s) => {
                        const badge = TRIGGER_BADGES[s.trigger] ?? TRIGGER_BADGES.chat;
                        const m = s.input_manifest;
                        const manifestOpen = expandedManifest === s.id;
                        const isChat = s.triggered_by_message !== null;

                        const cl = s.changelog ?? [];
                        const isInitial = cl[0] === "Initial generation";

                        const created = new Date(s.created_at);
                        const dateStr =
                          created.toLocaleDateString("ru-RU", { day: "numeric", month: "short" }) +
                          " " +
                          created.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });

                        // Output language badge. Prefer the manifest (accurate per-version,
                        // populated for new snapshots); fall back to the report's own
                        // language, which is set for every translated report variant.
                        const lang =
                          m?.refinement_context?.output_language || m?.generation?.language || r.language;

                        // Sources delta pills derived from the changelog text.
                        const pills: { label: string; cls: string }[] = [];
                        if (!isInitial) {
                          if (cl.some((c) => c.toLowerCase().includes("instruction")))
                            pills.push({ label: "+instruction", cls: "bg-blue-100 text-blue-700" });
                          if (cl.some((c) => c.includes("Visual context")))
                            pills.push({ label: "+visual_ctx", cls: "bg-green-100 text-green-700" });
                          if (cl.some((c) => c.includes("Brief")))
                            pills.push({ label: "+brief", cls: "bg-purple-100 text-purple-700" });
                          if (cl.some((c) => c.includes("Role") || c.includes("Scenario")))
                            pills.push({ label: "+config", cls: "bg-amber-100 text-amber-700" });
                          if (cl.some((c) => c.includes("Context updated")))
                            pills.push({ label: "+context", cls: "bg-teal-100 text-teal-700" });
                          if (cl.some((c) => c.includes("Speakers updated")))
                            pills.push({ label: "+speakers", cls: "bg-pink-100 text-pink-700" });
                        }

                        // Context = user-facing manifest data only (brief + refine context
                        // fields + genuine user instruction; system descriptors suppressed).
                        const briefData = (m?.brief?.data ?? {}) as Record<string, unknown>;
                        const briefEntries = Object.entries(briefData).filter(
                          ([k, v]) =>
                            !REFINEMENT_CONTEXT_KEYS.has(k) && v !== null && v !== "" && v !== undefined,
                        );
                        const rc = (m?.refinement_context ?? {}) as Record<string, unknown>;
                        const rcEntries = Object.entries(rc).filter(
                          ([, v]) => v && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== ""),
                        );
                        const SYSTEM_INSTRUCTIONS = /^(Refine panel regeneration|Reverted to v\d+.*)$/;
                        const userInstruction =
                          m?.instruction && !SYSTEM_INSTRUCTIONS.test(m.instruction) ? m.instruction : null;
                        const hasContext =
                          briefEntries.length > 0 || rcEntries.length > 0 || !!userInstruction;

                        // Card title is always derived from the changelog — the linked
                        // chat message is shown separately as a quote, not as the title.
                        // Strip the "New instruction:" changelog prefix and truncate.
                        const rawTitle = cl.length > 0 ? cl[0] : "Initial generation";
                        const strippedTitle = rawTitle.replace(/^New instruction:\s*/i, "");
                        const titleText =
                          strippedTitle.length > 50 ? strippedTitle.slice(0, 50) + "..." : strippedTitle;
                        const chatQuote =
                          s.triggered_by_message?.instruction &&
                          !SYSTEM_INSTRUCTIONS.test(s.triggered_by_message.instruction)
                            ? s.triggered_by_message.instruction
                            : null;

                        return (
                          <div
                            key={s.id}
                            className={`border bg-muted/20 px-2 py-1.5 ${
                              isChat ? "border-l-[3px] border-l-blue-500 rounded-r-lg" : "rounded-lg"
                            }`}
                          >
                            {/* a) Header: version + filtered title + trigger badge + date */}
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-sm font-medium min-w-0 break-words">
                                v{s.version}: {titleText}
                              </span>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {lang && (
                                  <span className="text-xs uppercase px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                    {String(lang)}
                                  </span>
                                )}
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${badge.cls}`}>
                                  {badge.label}
                                </span>
                                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                  {dateStr}
                                </span>
                              </div>
                            </div>

                            {/* b) Changelog */}
                            {isInitial ? (
                              <p className="text-[11px] text-gray-400 italic mt-1">Initial generation</p>
                            ) : (
                              cl.length > 0 && (
                                <div className="text-[11px] text-muted-foreground mt-1 space-y-0.5">
                                  {cl.map((c, i) => (
                                    <div key={i}>• {c}</div>
                                  ))}
                                </div>
                              )
                            )}

                            {/* c) Linked chat message — quote referencing the dialogue */}
                            {chatQuote && (
                              <p className="flex items-start gap-1 text-xs text-gray-500 italic mt-1">
                                <MessageSquare className="w-3 h-3 shrink-0 mt-0.5" />
                                <span>{chatQuote.slice(0, 120)}</span>
                              </p>
                            )}

                            {/* d) Sources delta pills */}
                            {pills.length > 0 && (
                              <div className="flex flex-row flex-wrap gap-1.5 mt-1">
                                {pills.map((p) => (
                                  <span
                                    key={p.label}
                                    className={`text-[10px] px-1.5 py-0.5 rounded-full ${p.cls}`}
                                  >
                                    {p.label}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* e) Context (expandable) — user-facing inputs only */}
                            {hasContext && (
                              <>
                                <button
                                  className="text-[10px] text-gray-400 hover:text-gray-600 mt-1"
                                  onClick={() => setExpandedManifest(manifestOpen ? null : s.id)}
                                >
                                  {manifestOpen ? "▾" : "▸"} Context
                                </button>
                                {manifestOpen && (
                                  <div className="border border-dashed border-gray-300 rounded px-2 py-1.5 mt-1 text-[11px] space-y-0.5">
                                    {rcEntries.map(([k, v]) => (
                                      <div key={k}>
                                        <span className="font-medium capitalize">
                                          {k.replace(/_/g, " ")}:
                                        </span>{" "}
                                        {Array.isArray(v) ? v.join(", ") : String(v)}
                                      </div>
                                    ))}
                                    {briefEntries.map(([k, v]) => (
                                      <div key={k}>
                                        <span className="font-medium capitalize">
                                          {k.replace(/_/g, " ")}:
                                        </span>{" "}
                                        {String(v)}
                                      </div>
                                    ))}
                                    {userInstruction && !SYSTEM_INSTRUCTIONS.test(userInstruction) && (
                                      <div className="text-gray-600 italic">{String(userInstruction)}</div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          );
        })}

        {!isLoading && personas.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6">No reports yet.</p>
        )}
      </div>
    </div>
  );
}
